import React from "react";
import { Check, X, Clock, X as CloseIcon } from "lucide-react";

const PaymentSlipModal = ({ isOpen, onClose, paymentData, status = "success" }) => {
  if (!isOpen) return null;

  // Default mock data if no props are passed
  const data = paymentData || {
    order_id: "ORD-99283109",
    client_txn_id: "TXN-847299102",
    requested_at: "July 20, 2026",
    paid_at: status === "success" ? "July 20, 2026" : null,
    amount: "₹24,500.00",
    user_id: "88291",
    paymentable_id: "PAY-5512",
    status_id: "ST-01",
  };

  // Normalize status to handle varied case inputs (e.g., "Success", "FAILED")
  const normalizedStatus = status?.toLowerCase();

  // Dynamic UI configuration based on status
  const statusConfig = {
    success: {
      gradient: "from-emerald-950 via-emerald-900 to-emerald-800",
      blob: "bg-emerald-400",
      iconContainer: "bg-emerald-500",
      textSubtle: "text-emerald-200",
      title: "Payment Successful",
      Icon: Check,
      amountColor: "text-white",
    },
    failed: {
      gradient: "from-rose-950 via-rose-900 to-rose-800",
      blob: "bg-rose-400",
      iconContainer: "bg-rose-500",
      textSubtle: "text-rose-200",
      title: "Payment Failed",
      Icon: X,
      amountColor: "text-white",
    },
    pending: {
      gradient: "from-amber-950 via-amber-900 to-amber-800",
      blob: "bg-amber-400",
      iconContainer: "bg-amber-500",
      textSubtle: "text-amber-200",
      title: "Payment Pending",
      Icon: Clock,
      amountColor: "text-white",
    },
  };

  // Fallback to success if an unknown status is passed
  const config = statusConfig[normalizedStatus] || statusConfig.success;
  const { Icon } = config;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Immersive Glassmorphism Backdrop */}
      <div
        className="absolute inset-0 bg-[#0a1128]/60 backdrop-blur-md transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal Container - 3D Layered Composition */}
      <div className="relative w-full max-w-md bg-white rounded-[2rem] shadow-2xl shadow-blue-900/40 overflow-hidden transform transition-all">
        
        {/* Dynamic Header with Mesh/Liquid Gradient Feel */}
        <div
          className={`relative bg-gradient-to-br ${config.gradient} px-8 py-10 text-center overflow-hidden transition-colors duration-500`}
        >
          {/* Subtle light leaks for a liquid glass effect */}
          <div className="absolute inset-0 w-full h-full opacity-30 pointer-events-none">
            <div className="absolute -top-12 -left-12 w-36 h-36 bg-white rounded-full mix-blend-overlay filter blur-2xl"></div>
            <div
              className={`absolute bottom-0 -right-10 w-48 h-48 ${config.blob} rounded-full mix-blend-overlay filter blur-3xl transition-colors duration-500`}
            ></div>
          </div>

          {/* Explicit Close Button */}
          <button
            onClick={onClose}
            className="absolute top-5 right-5 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-md border border-white/10 transition-colors focus:outline-none z-10"
            aria-label="Close modal"
          >
            <CloseIcon className="w-5 h-5" strokeWidth={2.5} />
          </button>

          {/* Dynamic Status Icon */}
          <div
            className={`relative w-16 h-16 ${config.iconContainer} border-2 border-white/50 border-dashed backdrop-blur-lg rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg transition-colors duration-500`}
          >
            <Icon className="w-8 h-8 text-white" strokeWidth={3} />
          </div>

          <h2
            className={`text-xs font-semibold ${config.textSubtle} uppercase tracking-widest mb-2 transition-colors duration-500`}
          >
            {config.title}
          </h2>
          <div className={`text-4xl font-extrabold tracking-tight ${config.amountColor}`}>
            {data.amount}
          </div>
        </div>

        {/* Crisp White Slip Body */}
        <div className="p-8 bg-white">
          <div className="space-y-4">
            {/* Order ID */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-100">
              <span className="text-sm text-gray-500 font-medium">Order ID</span>
              <span
                className="text-sm text-blue-950 font-semibold truncate max-w-[180px] sm:max-w-[220px]"
                title={data.order_id}
              >
                {data.order_id}
              </span>
            </div>

            {/* Client Transaction ID */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-100">
              <span className="text-sm text-gray-500 font-medium">
                Client Txn ID
              </span>
              <span className="text-sm text-blue-950 font-semibold">
                {data.client_txn_id}
              </span>
            </div>

            {/* Requested At */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-100">
              <span className="text-sm text-gray-500 font-medium">
                Requested Date
              </span>
              <span className="text-sm text-blue-950 font-semibold">
                {data.requested_at}
              </span>
            </div>

            {/* Paid At */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-100">
              <span className="text-sm text-gray-500 font-medium">Paid Date</span>
              <span
                className={`text-sm font-semibold ${
                  normalizedStatus === "success"
                    ? "text-blue-950"
                    : normalizedStatus === "failed"
                    ? "text-rose-600"
                    : "text-amber-600"
                }`}
              >
                {data.paid_at || (normalizedStatus === "failed" ? "Failed" : "Pending")}
              </span>
            </div>

            {/* User ID */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-100">
              <span className="text-sm text-gray-500 font-medium">User ID</span>
              <span className="text-sm text-blue-950 font-semibold">
                #{data.user_id}
              </span>
            </div>

            {/* Paymentable ID */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-100">
              <span className="text-sm text-gray-500 font-medium">
                Paymentable ID
              </span>
              <span className="text-sm text-blue-950 font-semibold">
                {data.paymentable_id}
              </span>
            </div>

            {/* Status ID */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500 font-medium">Status ID</span>
              <span className="text-sm text-blue-950 font-semibold">
                {data.status_id}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSlipModal;